import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar, 
  Home,
  Key,
  Edit,
  Save,
  X,
  CheckCircle
} from 'lucide-react';
import { tenantPortalAPI } from '../services/api';

const TenantProfile = () => {
  const [tenantData, setTenantData] = useState(null);
  const [propertyData, setPropertyData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const tenantId = localStorage.getItem('tenant_id') || sessionStorage.getItem('tenant_id');
  const isAuthenticated = !!(localStorage.getItem('tenant_token') || sessionStorage.getItem('tenant_token'));

  useEffect(() => {
    if (!isAuthenticated || !tenantId) return;

    const fetchTenantData = async () => {
      try {
        setLoading(true);
        // Get tenant information using tenant-specific endpoint
        const headers = tenantPortalAPI.getAuthHeaders();
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tenant/profile`, {
          headers
        });
        
        if (response.ok) {
          const tenant = await response.json();
          setTenantData(tenant);
          setEditData({
            name: tenant.name,
            email: tenant.email,
            phone: tenant.phone
          });

          // Property information is now included in tenant profile response
          if (tenant.property_name) {
            setPropertyData({
              name: tenant.property_name,
              address: tenant.property_address,
              id: tenant.property_id
            });
          }
        }
      } catch (error) {
        console.error('Failed to fetch tenant data:', error);
        setMessage('Failed to load profile information');
      } finally {
        setLoading(false);
      }
    };

    fetchTenantData();
  }, [isAuthenticated, tenantId]);

  const handleSave = async () => {
    if (!tenantData) return;
    setSaving(true);
    setMessage('');
    
    try {
      // Call tenant-specific profile update API
      const headers = tenantPortalAPI.getAuthHeaders();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tenant/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...headers
        },
        body: JSON.stringify(editData)
      });

      if (!response.ok) {
        throw new Error('Failed to update profile');
      }

      const updatedTenant = await response.json();
      
      // Update local tenant data
      setTenantData({
        ...tenantData,
        ...updatedTenant
      });
      
      setIsEditing(false);
      setMessage('Profile updated successfully!');
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Failed to update profile:', error);
      setMessage('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditData({
      name: tenantData.name,
      email: tenantData.email,
      phone: tenantData.phone
    });
    setIsEditing(false);
    setMessage('');
  };

  if (!isAuthenticated) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6 text-center">
            <User className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600">Please sign in to view your profile.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6 text-center">
            <div className="animate-spin h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading profile...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!tenantData) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6 text-center">
            <User className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600">Profile information not found.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
        {!isEditing ? (
          <Button onClick={() => setIsEditing(true)}>
            <Edit className="h-4 w-4 mr-2" />
            Edit Profile
          </Button>
        ) : (
          <div className="flex space-x-2">
            <Button onClick={handleSave} disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button variant="outline" onClick={handleCancel}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </div>
        )}
      </div>

      {message && (
        <Card className={`border-2 ${message.includes('success') ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
          <CardContent className="p-4">
            <div className="flex items-center">
              {message.includes('success') ? (
                <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
              ) : (
                <X className="h-5 w-5 text-red-600 mr-2" />
              )}
              <span className={message.includes('success') ? 'text-green-800' : 'text-red-800'}>
                {message}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Personal Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <User className="h-5 w-5 mr-2" />
              Personal Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">Full Name</Label>
              {isEditing ? (
                <Input
                  id="name"
                  value={editData.name}
                  onChange={(e) => setEditData({...editData, name: e.target.value})}
                />
              ) : (
                <p className="text-sm text-gray-900 mt-1">{tenantData.name}</p>
              )}
            </div>
            
            <div>
              <Label htmlFor="email">Email Address</Label>
              {isEditing ? (
                <Input
                  id="email"
                  type="email"
                  value={editData.email}
                  onChange={(e) => setEditData({...editData, email: e.target.value})}
                />
              ) : (
                <p className="text-sm text-gray-900 mt-1">{tenantData.email}</p>
              )}
            </div>
            
            <div>
              <Label htmlFor="phone">Phone Number</Label>
              {isEditing ? (
                <Input
                  id="phone"
                  value={editData.phone}
                  onChange={(e) => setEditData({...editData, phone: e.target.value})}
                />
              ) : (
                <p className="text-sm text-gray-900 mt-1">{tenantData.phone}</p>
              )}
            </div>

            <div>
              <Label>Account Status</Label>
              <div className="mt-1">
                <Badge variant={tenantData.status === 'active' ? 'default' : 'secondary'}>
                  {tenantData.status || 'Active'}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Lease Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Home className="h-5 w-5 mr-2" />
              Lease Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Property</Label>
              <p className="text-sm text-gray-900 mt-1">
                {propertyData?.name || 'Property information not available'}
              </p>
              {propertyData?.address && (
                <p className="text-xs text-gray-600 mt-1 flex items-center">
                  <MapPin className="h-3 w-3 mr-1" />
                  {propertyData.address}
                </p>
              )}
            </div>

            <div>
              <Label>Unit</Label>
              <p className="text-sm text-gray-900 mt-1">{tenantData.unit || 'N/A'}</p>
            </div>

            <div>
              <Label>Monthly Rent</Label>
              <p className="text-sm text-gray-900 mt-1">
                ${(tenantData.rent_amount || 0).toLocaleString()}
              </p>
            </div>

            {tenantData.lease_start && (
              <div>
                <Label>Lease Start Date</Label>
                <p className="text-sm text-gray-900 mt-1 flex items-center">
                  <Calendar className="h-3 w-3 mr-1" />
                  {new Date(tenantData.lease_start).toLocaleDateString()}
                </p>
              </div>
            )}

            {tenantData.lease_end && (
              <div>
                <Label>Lease End Date</Label>
                <p className="text-sm text-gray-900 mt-1 flex items-center">
                  <Calendar className="h-3 w-3 mr-1" />
                  {new Date(tenantData.lease_end).toLocaleDateString()}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Account Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Key className="h-5 w-5 mr-2" />
              Account Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Tenant ID</Label>
              <p className="text-sm text-gray-900 mt-1 font-mono">
                {(tenantData.id || tenantData._id)?.substring(0, 8)}...
              </p>
            </div>

            <div>
              <Label>Access Code</Label>
              <p className="text-sm text-gray-900 mt-1 font-mono">
                {tenantData.access_code ? '••••••••' : 'Not available'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Contact property management to reset access code
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Emergency Contact - placeholder for future */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Phone className="h-5 w-5 mr-2" />
              Emergency Contact
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center py-8 text-gray-500">
              <Phone className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Emergency contact information</p>
              <p className="text-sm">Coming soon</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TenantProfile;